
import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Building2, CreditCard, RefreshCw, AlertCircle } from 'lucide-react';

interface AccountSummary {
  totalEstimates: number;
  totalInvoices: number;
  totalPaid: number;
  totalOutstanding: number;
  lastSyncAt?: string;
  syncStatus: 'synced' | 'pending' | 'error';
}

interface WaveAccountOverviewProps {
  accountSummary?: AccountSummary;
  waveCustomerId?: string;
}

const WaveAccountOverview: React.FC<WaveAccountOverviewProps> = ({
  accountSummary,
  waveCustomerId
}) => {
  const getSyncStatusBadge = (status: string) => {
    switch (status) {
      case 'synced':
        return <Badge className="bg-green-500">Synced</Badge>;
      case 'pending':
        return <Badge className="bg-yellow-500">Syncing</Badge>;
      case 'error':
        return <Badge className="bg-red-500">Sync Error</Badge>;
      default:
        return <Badge variant="secondary">Unknown</Badge>;
    }
  };

  return (
    <Card className="border-blue-200 bg-gradient-to-r from-blue-50 to-indigo-50">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-blue-800">
          <Building2 className="h-5 w-5" />
          Your Billing Account Overview
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <CreditCard className="h-4 w-4 text-blue-600" />
              <span className="text-sm font-medium">Account Status</span>
            </div>
            <div className="text-lg font-semibold text-blue-800">
              {waveCustomerId ? 'Connected' : 'Not Connected'}
            </div>
            {waveCustomerId && (
              <div className="text-xs text-gray-600">
                Billing ID: {waveCustomerId.slice(-8)}
              </div>
            )}
          </div>

          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <RefreshCw className="h-4 w-4 text-blue-600" />
              <span className="text-sm font-medium">Sync Status</span>
            </div>
            <div>
              {accountSummary ? (
                getSyncStatusBadge(accountSummary.syncStatus)
              ) : (
                <Badge variant="secondary">No Data</Badge>
              )}
            </div>
            {accountSummary?.lastSyncAt && (
              <div className="text-xs text-gray-600">
                Last sync: {new Date(accountSummary.lastSyncAt).toLocaleDateString()}
              </div>
            )}
          </div>

          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <AlertCircle className="h-4 w-4 text-blue-600" />
              <span className="text-sm font-medium">Account Health</span>
            </div>
            <div className="text-lg font-semibold text-green-600">
              {accountSummary ? 'Active' : 'Pending Setup'}
            </div>
            {accountSummary && (
              <div className="text-xs text-gray-600">
                {accountSummary.totalEstimates + accountSummary.totalInvoices} total documents
              </div>
            )}
          </div>
        </div>

        {accountSummary && (
          <div className="mt-4 pt-4 border-t border-blue-200">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
              <div>
                <div className="text-xl font-bold text-blue-600">
                  ${accountSummary.totalPaid.toFixed(2)}
                </div>
                <div className="text-xs text-gray-600">Total Paid</div>
              </div>
              <div>
                <div className="text-xl font-bold text-orange-600">
                  ${accountSummary.totalOutstanding.toFixed(2)}
                </div>
                <div className="text-xs text-gray-600">Outstanding</div>
              </div>
              <div>
                <div className="text-xl font-bold text-green-600">
                  {accountSummary.totalEstimates}
                </div>
                <div className="text-xs text-gray-600">Estimates</div>
              </div>
              <div>
                <div className="text-xl font-bold text-purple-600">
                  {accountSummary.totalInvoices}
                </div>
                <div className="text-xs text-gray-600">Invoices</div>
              </div>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default WaveAccountOverview;
